import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  Fab,
  DialogContent,
  Button,
  CircularProgress,
  Box,
} from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { IVehicleAvailability } from ".";
import { DateTime as d, DateTime } from "luxon";
import { TestDriveTypes } from "../../../../../reducers/testDriveBooking/types";
import { useSelector } from "react-redux";
import { IAppState } from "../../../../../store";
import { getLocalizedDateFormat, getLocalizedDateFormatString } from "../../../../../utils/localized.syntex";
import { DATE_TYPE } from "../../../../utils";
import { useTranslation } from "react-i18next";
interface IProps {
  dialogOpen: boolean;
  bookingType: string;
  loading: boolean;
  vehicleAvailabilityPickUpSlots: IVehicleAvailability[]
  vehicleAvailabilityDropOffSlots: IVehicleAvailability[];
  handleDialogState: () => void;
  fetchAvailability: any;
  updateSlot: (data: string) => void;
  updateSelectedDate: (selectedDate: string) => void;
  setPickupSlotData?: (
    driverId: string,
    slotId: string,
    slotTime: string
  ) => void;
  setDropOffSlotData?: (
    driverId: string,
    slotId: string,
    slotTime: string
  ) => void;
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    [theme.breakpoints.up("md")]: {
      flexDirection: "column",
    },
    gap: theme.spacing(2),
  },
  selectedTimeSlot: {
    maxWidth: "100px",
    minWidth: "100px",
    "& .MuiChip-clickable:focus": {
      backgroundColor: "var(--primary-color)",
    },
    backgroundColor: "var(--primary-color)",
  },
  proceedButton: {
    marginBottom: theme.spacing(0.5),
    borderRadius: 0,
  },
  proceedIcon: {
    marginRight: theme.spacing(1),
  },
  listItem: {
    maxWidth: "100px",
  },
  timeSlotContainer: {
    maxHeight: "250px",
    overflow: "auto",
  },
  root: {
    maxWidth: 600,
    margin: "auto",
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  timeSlot: {
    marginBottom: theme.spacing(0.5),
  },
  selected: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  btnContainer: {
    minWidth: "120px",
  },
  calendarContainer: {
    minWidth: "310px",
  },
  slotsContainer: {
    minWidth: "400px",
  },
  dialog: {
    "& .MuiDialog-container": {
      "& .MuiPaper-root": {
        width: "100%",
        maxWidth: "760px",
      },
    },
  },
}));

export const CalendarTimeSlotSelector: React.FC<IProps> = ({
  dialogOpen,
  bookingType,
  loading,
  vehicleAvailabilityDropOffSlots,
  vehicleAvailabilityPickUpSlots,
  handleDialogState,
  fetchAvailability,
  updateSlot,
  updateSelectedDate,
  setPickupSlotData,
  setDropOffSlotData
}) => {
  const classes = useStyles();
  const [selectedDate, setSelectedDate] = useState<string>(
    d.now().toUTC().toISO()
  );
  const [disableSlotSelector, setSlotSelectorStatus] = useState<boolean>(false);
  const [selectedDropOffSlot, setSelectedDropOffSlot] = useState<number>(0);
  const [selectedPickUpSlot, setSelectedPickupSlot] = useState<number>(0);
  const [isDriverAvailableForPickup, setIsDriverAvailableForPickUp] = useState<boolean>(false)
  const [isDriverAvailableForDropOff, setIsDriverAvailableForDropOff] = useState<boolean>(false)
  const organisation = useSelector(
    (state: IAppState) =>
      state.consumerWebsiteReducer.consumerWebsite.organisation
  );
  const country = organisation!.address.country;
  const logisticsEnabled = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite.tenancy.logisticsEnabled);
  const {t} = useTranslation();
  const testDriveBookingState = useSelector((state: IAppState) => state.testDriveBookingReducer);
  const appSetting = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite)
  const currentBranch = appSetting.branches.find((branch) => branch.id === testDriveBookingState.branchId)

  const getHolidays = (startDate: d, endDate: d): string[] => {
    const holidays: string[] = [];
    let currentDate = startDate;

    while (currentDate <= endDate) {
      holidays.push(getLocalizedDateFormat(country, currentDate.toUTC().toISO(), DATE_TYPE.CONDENSED));
      currentDate = currentDate.plus({ days: 1 });
    }

    return holidays;
  };

  const getDisabledDates = (): (number | string)[] => {
    let disabledDatesArray: (number | string)[] = [];
    if (currentBranch?.operatingHours?.length) {
      for (const opHr of currentBranch.operatingHours) {
        if (opHr.closed) {
          disabledDatesArray.push(opHr.dayIndex);
        }
      }
    }
    if (currentBranch?.holidays?.length) {
      for (const holiday of currentBranch.holidays) {
        const dates = getHolidays(
          d.fromFormat(holiday.startDate, getLocalizedDateFormatString(country, DATE_TYPE.CONDENSED)),
          d.fromFormat(holiday.endDate, getLocalizedDateFormatString(country, DATE_TYPE.CONDENSED))
        );
        disabledDatesArray = [...disabledDatesArray, ...dates];
      }
    }

    return disabledDatesArray;
  };

  const shouldDisableDate = (date: MaterialUiPickersDate): boolean => {
    if (!date || !currentBranch) return false;
    const dateTime = d.fromJSDate(date.toDate());
    const weekday = dateTime.weekday;
    const formattedDate = getLocalizedDateFormat(country, dateTime.toUTC().toISO(), DATE_TYPE.CONDENSED);
    const disabledDates = getDisabledDates();
    return disabledDates.includes(weekday) || disabledDates.includes(formattedDate);
  };

  const handleDateChange = (date: MaterialUiPickersDate) => {
    setSelectedDate(date?.toISOString() || "");
    updateSelectedDate(date?.toISOString() || "")
    fetchAvailability(date?.toISOString() || "");
    setSelectedDropOffSlot(0);
    setSelectedPickupSlot(0);
  };

  const handleSelect = () => {
    if (selectedDate && vehicleAvailabilityDropOffSlots.length > 0 && selectedDropOffSlot === 0) {
      const slot = vehicleAvailabilityDropOffSlots[0]
      if (setDropOffSlotData) {
        setDropOffSlotData(slot.driverId, slot.slotId, slot.timeSlot)
      }
      const normalizedDate = DateTime.fromISO(selectedDate, { setZone: true }).toUTC()
      const formattedDate = getLocalizedDateFormat(country, normalizedDate.toISO(), DATE_TYPE.CONDENSED)
      const updatedSlot = `${formattedDate} | ${slot.timeSlot}`

      updateSlot(updatedSlot)
    }
    handleDialogState()
  }

  const handleDropOffSlotSelection = (
    slot: IVehicleAvailability,
    selectedSlotIndex: number
  ) => {
    if (setDropOffSlotData) {
      setDropOffSlotData(slot.driverId, slot.slotId, slot.timeSlot);
    }
    const normalizedDate = DateTime.fromISO(selectedDate, { setZone: true }).toUTC();
    const formattedDate = getLocalizedDateFormat(
      country, 
      normalizedDate.toISO(),
      DATE_TYPE.CONDENSED
    );
    const updatedSlot = `${formattedDate} | ${slot.timeSlot}`
    updateSlot(updatedSlot)
    setSelectedDropOffSlot(selectedSlotIndex);
  };

  return (
    <Dialog
      onClose={handleDialogState}
      aria-labelledby="slot-selector"
      open={dialogOpen}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>
        <Typography variant={"h3"}>{t("root:selectDateTimeSlot")}</Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container xs={12} spacing={2}>
          <Grid item xs={4}>
            <DatePicker
              disableToolbar
              variant="static"
              disablePast
              value={selectedDate}
              onChange={handleDateChange}
              shouldDisableDate={shouldDisableDate}
            />
          </Grid>
          <Grid item xs={8}>
            <Grid
              container
              alignContent="center"
              justify="space-between"
              alignItems="center"
            >
              <Grid container className={classes.slotsContainer} spacing={2}>
                {loading && (
                  <Grid container item xs={12} spacing={1}>
                    <CircularProgress />
                  </Grid>
                )}

                {!loading && vehicleAvailabilityDropOffSlots ? (
                  <Grid item xs={6}>
                    <Grid item xs={12}><Typography variant={"subtitle1"}>{t("root:selectDeliverySlot")}</Typography></Grid>
                    <Grid item xs={12}><Box mt={1}></Box></Grid>
                    <Grid container spacing={2}>
                      {vehicleAvailabilityDropOffSlots.map((slot, index) => (
                        <Grid
                          item
                          xs={12}
                          sm={2}
                          key={index}
                          className={classes.btnContainer}
                        >
                          <Button
                            variant="outlined"
                            fullWidth
                            className={`${classes.timeSlot} ${selectedDropOffSlot === index ? classes.selected : ""
                              }`}
                            onClick={() => handleDropOffSlotSelection(slot, index)}
                          >
                            {slot.timeSlot}
                          </Button>
                        </Grid>
                      ))}
                      {
                        logisticsEnabled && !isDriverAvailableForDropOff && (
                          <Grid item xs={12}><Typography><strong>{t("root:note")}:</strong> {t("root:driverNotAvailable")}.</Typography></Grid>
                        )
                      }
                    </Grid>
                  </Grid>
                ) : <></>}
                {!loading && !vehicleAvailabilityDropOffSlots.length && (
                  <Grid container item xs={12} spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="body1" style={{ marginTop: "16px" }}>
                        {t("root:vehicleNotAvailable")}.
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body1" style={{ marginTop: "16px" }}>
                        {t("root:selectDifferentDate")}.
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container justify="center" style={{ marginTop: "20px" }}>
          <Fab
            aria-label="proceed-with-vehicle-selection"
            variant="extended"
            size="medium"
            className={classes.proceedButton}
            onClick={handleSelect}
            style={{
              backgroundColor: disableSlotSelector
                ? "#e0e0e0"
                : "var(--primary-color)",
            }}
            disabled={disableSlotSelector}
          >
            {t("root:selectSlot")}
          </Fab>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
